// ============================================
// GoPredict S360 — Taiga copy of GoPredict's Symbol 360 view, for one TAIGA
// symbol (platform + symbol) through its NexDay mapping. Second tab of the
// Predictions page (/predictions?view=s360&platform=MT5&symbol=GOLD).
// Search lists Taiga's mapped symbols only. Tile rules: handoff v1.2 section 5.3
// (GoPredict's own). Prices: 3 decimals for JPY symbols, otherwise 4.
//
// Read-only. Data: GET /api/v1/predictions/symbol360?symbol=<NexDay symbol>
// and the search list GET /api/v1/predictions/symbol360/symbols.
// The NexDay symbol always travels in the query string ('/' and '#' occur).
// Live: reloads silently on each nexday.synced event (/ws/v1/nexday/events)
// and on a reconnect; no polling. The Refresh button stays.
// Times: Local (default on every open) or ET, by a toggle in the symbol header.
// NexDay's description / exchange are not shown (not the data source).
// ============================================

import { useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';

const API_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

class HttpError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: { 'Content-Type': 'application/json' } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = typeof (err as any)?.error === 'string' ? (err as any).error : `HTTP ${res.status}`;
    throw new HttpError(res.status, msg);
  }
  return res.json();
}

// ── Types ────────────────────────────────────────────────────

type Platform = 'MT5' | 'CTRADER';

interface S360SymbolRow {
  symbol: string;
  description: string;
  security_type: string;
  exchange: string;
  is_active: boolean;
  mappings: { platform: Platform; symbol: string }[];
}

interface S360Daily {
  target_date: string;
  predicted_strength: number | null;
  momentum: string | null;
  days_since_reversal: number | null;
  predicted_trend: string | null;
  predicted_trading_range: string | null;
  predicted_high: number | null;
  predicted_low: number | null;
  predicted_range: number | null;
  predicted_midpoint: number | null;
  predicted_onefourth: number | null;
  [k: string]: unknown;
}

interface S360Opportunity {
  target_date: string;
  opportunity: string | null;
  opportunity_direction: string | null;   // "UP" | "DOWN"
  conviction: string | null;
  [k: string]: unknown;
}

interface S360Intraday {
  target_time: string;
  predicted_high: number | null;
  predicted_low: number | null;
  trend: number | null;
  momentum: string | null;
  [k: string]: unknown;
}

interface S360Response {
  symbol: string;
  description: string;
  security_type: string;
  exchange: string;
  is_active: boolean;
  last_update: string | null;
  daily: S360Daily | null;
  /** NexDay's published field: STRONG_BULLISH | BULLISH | NEUTRAL | BEARISH | STRONG_BEARISH. */
  trend_direction: { value: string | null; alignment_score?: number | null } | null;
  opportunity: S360Opportunity | null;
  intraday: { timeframe: string; prediction: S360Intraday | null }[];
  mappings: { id: number; platform: Platform; symbol: string }[];
  generated_at: string;
}

// ── Palette (design system) ──────────────────────────────────

const C = {
  red:    '#d07070',
  green:  '#6aaa78',
  amber:  '#c09060',
  teal:   '#49b3b3',
  blue:   '#5b86b8',
  panel:  '#1a1a1c',
  panel2: '#2a292c',
  field:  '#232225',
  border: '#44454f',
  text:   '#E6E6E6',
  soft:   '#d2d6e2',
};

const MONO = { fontFamily: "'IBM Plex Mono', monospace" } as const;

// ── Formatting ───────────────────────────────────────────────

const platformLabel = (p: Platform) => (p === 'CTRADER' ? 'cTrader' : 'MT5');

const TIMEFRAMES: Record<string, { label: string; minutes: number }> = {
  '15min': { label: '15 Min', minutes: 15 },
  '30min': { label: '30 Min', minutes: 30 },
  '1hour': { label: '1 Hour', minutes: 60 },
  '2hour': { label: '2 Hour', minutes: 120 },
};

/** "2026-10-26" -> "Monday, October 26, 2026" (calendar date, no time-zone shift). */
function fmtTargetDate(d: string | null | undefined): string {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return d;
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString('en-US', {
    timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

type TzMode = 'local' | 'et';
const ET = 'America/New_York';
const tzOf = (mode: TzMode) => (mode === 'et' ? ET : undefined);   // undefined = browser's zone

/** Short zone label: "ET" or the browser's own (e.g. "GMT+1", "BST"). */
function tzLabel(mode: TzMode, at: Date = new Date()): string {
  if (mode === 'et') return 'ET';
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(at).find(p => p.type === 'timeZoneName');
    return part?.value ?? 'Local';
  } catch { return 'Local'; }
}

function fmtDateTime(iso: string | null | undefined, mode: TzMode): string {
  if (!iso) return '—';
  const t = new Date(iso);
  if (isNaN(t.getTime())) return iso;
  return `${t.toLocaleString('en-US', { timeZone: tzOf(mode), month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} ${tzLabel(mode, t)}`;
}

/** Window ending at target_time, starting one timeframe earlier. */
function fmtWindow(targetIso: string, minutes: number | undefined, mode: TzMode): string {
  const end = new Date(targetIso);
  if (isNaN(end.getTime())) return targetIso;
  const tz   = tzOf(mode);
  const time = (d: Date) => d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  const day  = end.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' });
  if (!minutes) return `${day} · ${time(end)} ${tzLabel(mode, end)}`;
  const start = new Date(end.getTime() - minutes * 60_000);
  return `${day} · ${time(start)} – ${time(end)} ${tzLabel(mode, end)}`;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** GoPredict's price rule: 3 decimals when the symbol contains JPY, otherwise 4. */
const priceDigits = (nexdaySymbol: string) => (/JPY/i.test(nexdaySymbol) ? 3 : 4);

const fmtPx = (v: number | null | undefined, digits: number) => (!isNum(v) ? '—' : v.toFixed(digits));

/**
 * Trading levels as GoPredict shows them, from the daily prediction.
 * q = predicted_onefourth (a quarter of the predicted range).
 *   SHORT: Take Profit low / low+q, Entry mid / mid+q, Stop Loss high+q / high
 *   LONG : mirrored — Take Profit high / high-q, Entry mid / mid-q, Stop Loss low-q / low
 * Pairs are (max, min) in GoPredict's labelling. SHORT verified against
 * GoPredict XAUUSD (TP 4021.7028 / 4039.3767, Entry 4057.0507 / 4074.7246,
 * SL 4110.0724 / 4092.3985). Direction: opportunity_direction, else the
 * daily predicted trend.
 */
function tradingLevels(d: S360Daily | null, op: S360Opportunity | null, digits: number) {
  if (!d || !isNum(d.predicted_high) || !isNum(d.predicted_low)) return null;
  const hi  = d.predicted_high, lo = d.predicted_low;
  const mid = isNum(d.predicted_midpoint) ? d.predicted_midpoint : (hi + lo) / 2;
  const q   = isNum(d.predicted_onefourth) ? d.predicted_onefourth : (hi - lo) / 4;
  const od  = (op?.opportunity_direction ?? '').toUpperCase();
  const tr  = (d.predicted_trend ?? '').toLowerCase();
  const side: 'LONG' | 'SHORT' | null =
    od === 'DOWN' ? 'SHORT' : od === 'UP' ? 'LONG'
    : tr === 'down' ? 'SHORT' : tr === 'up' ? 'LONG' : null;
  if (!side) return null;
  const r  = (v: number) => v.toFixed(digits);
  return side === 'SHORT'
    ? { side, tp: [r(lo), r(lo + q)],   entry: [r(mid), r(mid + q)], sl: [r(hi + q), r(hi)] }
    : { side, tp: [r(hi), r(hi - q)],   entry: [r(mid), r(mid - q)], sl: [r(lo - q), r(lo)] };
}

// ── Tile rules (handoff v1.2, 5.3) ───────────────────────────

/** Header description: "FXCM" removed, "SPO" shown as "Spot". */
const cleanDescription = (s: string | null | undefined) =>
  (s ?? '').replace(/\bFXCM\b/gi, '').replace(/\bSPO\b/g, 'Spot').replace(/\s+/g, ' ').trim();

/** Strength sub-label: > 0 Strengthening, < 0 Weakening, 0 Neutral. */
/**
 * Strength as GoPredict shows it: 2 decimals from 1 up (XAU/USD -37.26),
 * 4 decimals below 1 (FX pairs: -0.0007), so a small value never shows as
 * 0.00 or -0.00. Exactly 0 shows 0.00.
 */
function fmtStrength(v: number): string {
  if (v === 0) return '0.00';
  return Math.abs(v) >= 1 ? v.toFixed(2) : v.toFixed(4);
}

const strengthLabel = (v: number) => (v > 0 ? 'Strengthening' : v < 0 ? 'Weakening' : 'Neutral');

/** Momentum: contains "bullish" green, "bearish" red, else white. */
function momentumColor(v: string | null | undefined): string {
  const s = (v ?? '').toLowerCase();
  return s.includes('bullish') ? C.green : s.includes('bearish') ? C.red : C.text;
}

/** Trend: contains "up"/"bullish" green, "down"/"bearish" red, else white. */
function trendColor(v: string | null | undefined): string {
  const s = (v ?? '').toLowerCase();
  if (s.includes('up') || s.includes('bullish')) return C.green;
  if (s.includes('down') || s.includes('bearish')) return C.red;
  return C.text;
}

/** 0 "Reversed: Today", 1 "Reversed: 1 day ago", N "Reversed: N days ago", null none. */
function reversedText(n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  if (n === 0) return 'Reversed: Today';
  return `Reversed: ${n} day${n === 1 ? '' : 's'} ago`;
}

const dirOf = (v: string | null | undefined): 'UP' | 'DOWN' | 'NONE' => {
  const s = (v ?? '').toUpperCase();
  return s === 'UP' ? 'UP' : s === 'DOWN' ? 'DOWN' : 'NONE';
};
const dirColorOf = (d: 'UP' | 'DOWN' | 'NONE') => (d === 'UP' ? C.green : d === 'DOWN' ? C.red : C.text);

/** Intraday Pred Trend: > 0 "↑ Up" green, < 0 "↓ Down" red, 0 "→ Flat" white. */
function predTrend(v: number | null | undefined): { text: string; color: string } {
  if (!isNum(v)) return { text: '—', color: C.text };
  return v > 0 ? { text: '↑ Up', color: C.green } : v < 0 ? { text: '↓ Down', color: C.red } : { text: '→ Flat', color: C.text };
}

/** Intraday Momentum: bullish "↑ Bullish" green, bearish "↓ Bearish" red, else as-is white. */
function intradayMomentum(v: string | null | undefined): { text: string; color: string } {
  const s = (v ?? '').toLowerCase();
  if (s.includes('bullish')) return { text: '↑ Bullish', color: C.green };
  if (s.includes('bearish')) return { text: '↓ Bearish', color: C.red };
  return { text: v || '—', color: C.text };
}

// ── Favorites (per viewer, browser storage, max 10) ──────────

const FAV_KEY = 'taiga.gopredict.s360.favorites';
const FAV_MAX = 10;

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(FAV_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    // Keys are "MT5:GOLD" / "CTRADER:EURUSD"; older NexDay-only keys are dropped.
    return Array.isArray(arr)
      ? arr.filter((s) => typeof s === 'string' && /^(MT5|CTRADER):./.test(s)).slice(0, FAV_MAX)
      : [];
  } catch { return []; }
}

function writeFavorites(list: string[]) {
  try { window.localStorage.setItem(FAV_KEY, JSON.stringify(list.slice(0, FAV_MAX))); } catch { /* storage unavailable */ }
}

// ── Icons ────────────────────────────────────────────────────

const IcoStar = ({ filled, size = 14 }: { filled: boolean; size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round">
    <path d="M12 2.8l2.8 5.7 6.3.9-4.6 4.4 1.1 6.2L12 17.1 6.4 20l1.1-6.2L2.9 9.4l6.3-.9z" />
  </svg>
);

const IcoTarget = ({ size = 44 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={1.4}>
    <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5.5" /><circle cx="12" cy="12" r="2" />
  </svg>
);

const IcoSearch = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
  </svg>
);

const IcoRefresh = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 4v7h-7" />
  </svg>
);

const IcoClose = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// ── Small building blocks ────────────────────────────────────

function Tile({ label, children, sub, wide }: { label: string; children: ReactNode; sub?: ReactNode; wide?: boolean }) {
  return (
    <div className="rounded px-4 py-3 flex flex-col gap-1"
      style={{ backgroundColor: C.panel, border: `1px solid ${C.border}`, gridColumn: wide ? 'span 2' : undefined }}>
      <span className="text-xs" style={{ color: C.soft }}>{label}</span>
      <div className="text-base font-semibold" style={{ color: C.text }}>{children}</div>
      {sub && <div className="text-xs" style={{ color: C.soft }}>{sub}</div>}
    </div>
  );
}

// ============================================================
// COMPONENT
// ============================================================

/** A Taiga symbol on a platform. */
export interface TaigaSymbolRef { platform: Platform; symbol: string }

interface TaigaRow extends TaigaSymbolRef { nexday: string; security_type: string }

const keyOf = (r: TaigaSymbolRef) => `${r.platform}:${r.symbol}`;
const refOf = (k: string): TaigaSymbolRef | null => {
  const i = k.indexOf(':');
  if (i < 0) return null;
  const platform = k.slice(0, i) as Platform;
  return platform === 'MT5' || platform === 'CTRADER' ? { platform, symbol: k.slice(i + 1) } : null;
};

const PlatformTag = ({ p }: { p: Platform }) => (
  <span className="px-1.5 py-0.5 rounded font-medium flex-shrink-0"
    style={{ fontSize: 10, backgroundColor: C.field, border: `1px solid ${p === 'CTRADER' ? C.amber : C.teal}`,
      color: p === 'CTRADER' ? C.amber : C.teal }}>
    {platformLabel(p)}
  </span>
);

export function GoPredictS360({ selected, onSelect }: { selected: TaigaSymbolRef | null; onSelect: (s: TaigaSymbolRef | null) => void }) {
  const [symbols,   setSymbols]   = useState<S360SymbolRow[]>([]);
  const [listReady, setListReady] = useState(false);
  const [listErr,   setListErr]   = useState<string | null>(null);
  const [query,     setQuery]     = useState('');
  const [open,      setOpen]      = useState(false);
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());
  const [data,      setData]      = useState<S360Response | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [tzMode,    setTzMode]    = useState<TzMode>('local');   // Local on every open
  const boxRef = useRef<HTMLDivElement>(null);

  // Search list — once per mount.
  useEffect(() => {
    getJson<{ symbols: S360SymbolRow[] }>('/api/v1/predictions/symbol360/symbols')
      .then((r) => setSymbols(r.symbols ?? []))
      .catch((e: Error) => setListErr(e.message))
      .finally(() => setListReady(true));
  }, []);

  // Taiga's mapped symbols: one row per (platform, symbol), with its NexDay symbol.
  const rows = useMemo<TaigaRow[]>(() => {
    const out: TaigaRow[] = [];
    for (const s of symbols) for (const m of s.mappings ?? []) {
      out.push({ platform: m.platform, symbol: m.symbol, nexday: s.symbol, security_type: s.security_type });
    }
    return out.sort((a, b) => a.symbol.localeCompare(b.symbol) || a.platform.localeCompare(b.platform));
  }, [symbols]);

  const current = selected ? rows.find(r => r.platform === selected.platform && r.symbol === selected.symbol) ?? null : null;
  const nexday  = current?.nexday ?? null;
  const digits  = priceDigits(nexday ?? selected?.symbol ?? '');

  // The NexDay symbol on screen now: a response for another symbol (the user
  // switched while it was loading) is dropped.
  const nexdayRef = useRef<string | null>(null);
  useEffect(() => { nexdayRef.current = nexday; }, [nexday]);

  // silent: live reload. No spinner; a failed reload keeps what is on screen.
  const load = useCallback(async (sym: string, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const r = await getJson<S360Response>(`/api/v1/predictions/symbol360?symbol=${encodeURIComponent(sym)}`);
      if (nexdayRef.current !== sym) return;
      setData(r);
      if (silent) setError(null);
    } catch (e) {
      if (nexdayRef.current !== sym || silent) return;
      setData(null);
      const status = e instanceof HttpError ? e.status : 0;
      setError(
        status === 404 ? `${sym} is not a NexDay symbol.`
        : status === 503 ? 'NexDay predictions are unavailable: the NexDay service is not running.'
        : (e as Error).message || 'Failed to load predictions',
      );
    } finally {
      if (!silent && nexdayRef.current === sym) setLoading(false);
    }
  }, []);

  // Live updates (handoff v1.3, 5.5): one socket for the page's lifetime.
  // nexday.synced -> silent reload of the open symbol, at most once a second
  // (a daily cycle sends two events). On a REconnect, reload once, since a
  // sync may have been missed while disconnected (no snapshot is sent).
  useEffect(() => {
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let delay = 1_000;
    let opened = false;
    let lastReload = 0;
    let closed = false;

    const reload = () => {
      const sym = nexdayRef.current;
      if (!sym) return;
      const now = Date.now();
      if (now - lastReload < 1_000) return;
      lastReload = now;
      load(sym, true);
    };

    const connect = () => {
      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${scheme}//${window.location.host}/ws/v1/nexday/events`);
      ws.onopen = () => {
        delay = 1_000;
        if (opened) reload();
        opened = true;
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as { topic?: string };
          if (msg.topic === 'nexday.synced') reload();
        } catch { /* not JSON */ }
      };
      ws.onclose = () => {
        ws = null;
        if (closed) return;
        timer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30_000);
      };
      ws.onerror = () => { /* onclose follows */ };
    };

    connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, [load]);

  useEffect(() => {
    if (!selected) { setData(null); setError(null); return; }
    if (nexday) { load(nexday); return; }
    if (listReady) {
      setData(null);
      setError(listErr ?? `${selected.symbol} (${platformLabel(selected.platform)}) has no NexDay mapping. Map it on the Mappings tab.`);
    }
  }, [selected?.platform, selected?.symbol, nexday, listReady, listErr, load]);

  // Close the result list on an outside click.
  useEffect(() => {
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return rows
      .filter((r) => r.symbol.toLowerCase().includes(q) || r.nexday.toLowerCase().includes(q))
      .slice(0, 50);
  }, [rows, query]);

  const pick = (r: TaigaSymbolRef) => { setQuery(''); setOpen(false); onSelect({ platform: r.platform, symbol: r.symbol }); };

  const toggleFavorite = (s: string) => {
    setFavorites((prev) => {
      const next = prev.includes(s) ? prev.filter((x) => x !== s) : [s, ...prev].slice(0, FAV_MAX);
      writeFavorites(next);
      return next;
    });
  };

  const selKey = selected ? keyOf(selected) : null;
  const isFav = selKey ? favorites.includes(selKey) : false;
  const favFull = favorites.length >= FAV_MAX;

  // ── Search box + favorites line (shared by both states) ──
  const searchBlock = (
    <div className="space-y-2">
      <div ref={boxRef} className="relative" style={{ maxWidth: 520 }}>
        <div className="flex items-center gap-2 rounded px-3 py-2"
          style={{ backgroundColor: C.field, border: `1px solid ${open ? C.teal : '#606060'}` }}>
          <span style={{ color: C.soft }}><IcoSearch /></span>
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) pick(results[0]);
              if (e.key === 'Escape') setOpen(false);
            }}
            placeholder="Search MT5 or cTrader symbol..."
            aria-label="Search MT5 or cTrader symbol"
            className="flex-1 bg-transparent text-sm text-white focus:outline-none"
          />
        </div>
        {open && query.trim() && (
          <div className="absolute left-0 right-0 mt-1 rounded overflow-y-auto z-20"
            style={{ backgroundColor: C.panel, border: `1px solid ${C.border}`, maxHeight: 320 }}>
            {listErr ? (
              <p className="text-xs p-3" style={{ color: C.red }}>{listErr}</p>
            ) : results.length === 0 ? (
              <p className="text-xs p-3 text-center" style={{ color: C.soft }}>
                No mapped symbol matches. Only symbols mapped on the Mappings tab have predictions.
              </p>
            ) : results.map((r) => (
              <button key={keyOf(r)} onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-3 hover:bg-surface-hover transition-colors">
                <PlatformTag p={r.platform} />
                <span className="font-semibold" style={{ ...MONO, color: C.text }}>{r.symbol}</span>
                <span className="ml-auto flex-shrink-0" style={{ color: C.soft }}>
                  NexDay <span style={MONO}>{r.nexday}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span style={{ color: C.soft }}>Favorites:</span>
        {favorites.length === 0 ? (
          <span style={{ color: C.soft }}>none — use the star on a symbol to keep it here</span>
        ) : favorites.map((f) => {
          const r = refOf(f);
          if (!r) return null;
          const on = f === selKey;
          return (
            <button key={f} onClick={() => pick(r)}
              className="px-2 py-0.5 rounded transition-colors flex items-center gap-1.5"
              style={{
                fontSize: 11,
                backgroundColor: on ? '#163a3a' : C.field,
                color: on ? C.teal : C.text,
                border: `1px solid ${on ? C.teal : C.border}`,
              }}>
              <span style={MONO}>{r.symbol}</span>
              {r.platform === 'CTRADER' && <span style={{ color: C.amber }}>cTrader</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── Empty state ──
  if (!selected) {
    return (
      <div className="space-y-5">
        <h2 className="text-lg font-semibold" style={{ color: C.text }}>GoPredict Symbol 360</h2>
        {searchBlock}
        <div className="rounded flex flex-col items-center justify-center text-center py-14 px-6"
          style={{ backgroundColor: C.panel, border: `1px solid ${C.border}` }}>
          <span style={{ color: C.teal }}><IcoTarget /></span>
          <p className="text-base font-semibold mt-3" style={{ color: C.text }}>Select a Symbol</p>
          <p className="text-xs mt-1" style={{ color: C.soft }}>Search above or pick a favorite to see its predictions.</p>
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {['Daily Predictions & Trading Levels', 'Intraday Predictions (15m/30m/1h/2h)', 'Momentum & Strength Analysis'].map((h) => (
              <span key={h} className="text-xs px-3 py-1.5 rounded"
                style={{ backgroundColor: C.panel2, color: C.text, border: `1px solid ${C.border}` }}>
                {h}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Symbol view ──
  const d   = data?.daily ?? null;
  const op  = data?.opportunity ?? null;
  const td  = data?.trend_direction?.value ?? null;
  const opDir = dirOf(op?.opportunity_direction);
  const convictionColor = (op?.conviction ?? '').toLowerCase().startsWith('prime') ? dirColorOf(opDir) : C.text;
  // Suggestion 5.3: grey a row whose bar ended before the newest 15min bar's start.
  const p15 = data?.intraday.find(r => r.timeframe === '15min')?.prediction;
  const newest15Start = p15 ? new Date(p15.target_time).getTime() - 15 * 60_000 : null;
  const strength = d?.predicted_strength;
  const levels   = tradingLevels(d, op, digits);

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold" style={{ color: C.text }}>GoPredict Symbol 360</h2>
      {searchBlock}

      {/* Symbol header */}
      <div className="rounded px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2"
        style={{ backgroundColor: C.panel2, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => toggleFavorite(keyOf(selected))}
            disabled={!isFav && favFull}
            title={isFav ? 'Remove from favorites' : favFull ? `Favorites are full (${FAV_MAX})` : 'Add to favorites'}
            aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
            style={{ color: isFav ? C.amber : C.soft, opacity: !isFav && favFull ? 0.4 : 1 }}>
            <IcoStar filled={isFav} size={16} />
          </button>
          <PlatformTag p={selected.platform} />
          <span className="text-xl font-semibold" style={{ ...MONO, color: C.text }}>{selected.symbol}</span>
          {nexday && (
            <span className="text-xs" style={{ color: C.soft }}>NexDay <span style={{ ...MONO, color: C.text }}>{nexday}</span></span>
          )}
          {data && cleanDescription(data.description) && (
            <span className="text-sm" style={{ color: C.text }}>{cleanDescription(data.description)}</span>
          )}
          {data && !data.is_active && (
            <span className="px-1.5 py-0.5 rounded"
              style={{ fontSize: 10, backgroundColor: C.field, color: C.soft, border: `1px solid ${C.border}` }}>
              Inactive
            </span>
          )}
        </div>

        {data?.security_type && (
          <span className="text-xs" style={{ color: C.soft }}>{data.security_type}</span>
        )}

        <div className="flex items-center gap-3 ml-auto">
          <div role="radiogroup" aria-label="Time zone" className="inline-flex rounded overflow-hidden"
            style={{ border: '1px solid #606060' }}>
            {([['local', 'Local'], ['et', 'ET']] as const).map(([k, label]) => (
              <button key={k} type="button" role="radio" aria-checked={tzMode === k} onClick={() => setTzMode(k)}
                className="text-xs px-2.5 py-0.5 transition-colors"
                style={tzMode === k ? { backgroundColor: '#163a3a', color: C.teal } : { backgroundColor: C.field, color: C.soft }}>
                {label}
              </button>
            ))}
          </div>
          <span className="text-xs" style={{ color: C.soft }}>
            Last Update <span style={{ ...MONO, color: C.text }}>{fmtDateTime(data?.last_update, tzMode)}</span>
          </span>
          <button onClick={() => nexday && load(nexday)} disabled={loading || !nexday} title="Refresh" aria-label="Refresh"
            className="p-1 rounded transition-colors hover:bg-surface-hover"
            style={{ color: C.text, opacity: loading ? 0.5 : 1 }}>
            <IcoRefresh />
          </button>
          <button onClick={() => onSelect(null)} title="Close symbol" aria-label="Close symbol"
            className="p-1 rounded transition-colors hover:bg-surface-hover" style={{ color: C.text }}>
            <IcoClose />
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded px-4 py-3 text-sm"
          style={{ backgroundColor: '#2c1417', border: '1px solid #7a2f36', color: C.text }}>
          {error}
        </div>
      ) : loading && !data ? (
        <p className="text-sm py-10 text-center" style={{ color: C.soft }}>Loading predictions…</p>
      ) : data && (
        <>
          {/* Daily tiles */}
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
            <Tile label="Prediction Target Date" wide>
              <span style={MONO}>{d ? fmtTargetDate(d.target_date) : '—'}</span>
            </Tile>
            <Tile label="Strength"
              sub={isNum(strength) ? <span style={{ color: strength >= 0 ? C.green : C.red }}>{strengthLabel(strength)}</span> : null}>
              <span style={{ ...MONO, color: !isNum(strength) ? C.text : strength >= 0 ? C.green : C.red }}>
                {isNum(strength) ? fmtStrength(strength) : '—'}
              </span>
            </Tile>
            <Tile label="Momentum" sub={reversedText(d?.days_since_reversal)}>
              <span style={{ color: momentumColor(d?.momentum) }}>{d?.momentum || '—'}</span>
            </Tile>
            <Tile label="Trend">
              <span style={{ color: trendColor(d?.predicted_trend) }}>{d?.predicted_trend || '—'}</span>
            </Tile>
            <Tile label="Trading Range" wide>
              <span>{d?.predicted_trading_range || '—'}</span>
            </Tile>
            <Tile label="Opportunity">
              <span style={{ color: op?.opportunity ? dirColorOf(opDir) : C.text }}>
                {op?.opportunity
                  ? `${opDir === 'UP' ? '↑ ' : opDir === 'DOWN' ? '↓ ' : ''}${op.opportunity}`
                  : '—'}
              </span>
            </Tile>
            <Tile label="Trend Direction">
              <span>{td || '—'}</span>
            </Tile>
            <Tile label="Conviction">
              <span style={{ color: op?.conviction ? convictionColor : C.text }}>{op?.conviction || '—'}</span>
            </Tile>
          </div>

          {!d && (
            <p className="text-xs" style={{ color: C.soft }}>NexDay has no daily prediction for this symbol.</p>
          )}

          {/* Intraday */}
          <div className="rounded overflow-hidden" style={{ backgroundColor: C.panel, border: `1px solid ${C.border}` }}>
            <div className="px-4 py-2 text-sm font-semibold" style={{ color: C.text, backgroundColor: C.panel2, borderBottom: `1px solid ${C.border}` }}>
              Intraday Predictions
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left" style={{ color: C.soft }}>
                  <th className="px-4 py-2 font-medium">Timeframe</th>
                  <th className="px-4 py-2 font-medium">Target Time</th>
                  <th className="px-4 py-2 font-medium text-right">Pred High</th>
                  <th className="px-4 py-2 font-medium text-right">Pred Low</th>
                  <th className="px-4 py-2 font-medium">Pred Trend</th>
                  <th className="px-4 py-2 font-medium">Momentum</th>
                </tr>
              </thead>
              <tbody>
                {data.intraday.map(({ timeframe, prediction: p }) => {
                  const tf = TIMEFRAMES[timeframe];
                  const has = !!p && (isNum(p.predicted_high) || isNum(p.predicted_low));
                  const trend = predTrend(p?.trend);
                  const mom   = intradayMomentum(p?.momentum);
                  const stale = has && newest15Start !== null && new Date(p!.target_time).getTime() < newest15Start;
                  return (
                    <tr key={timeframe} style={{ borderTop: `1px solid ${C.border}`, opacity: stale ? 0.55 : 1 }}
                      title={stale ? 'Not refreshed by NexDay since this bar' : undefined}>
                      <td className="px-4 py-2" style={{ color: C.blue }}>{tf?.label ?? timeframe}</td>
                      {has ? (
                        <>
                          <td className="px-4 py-2" style={{ ...MONO, color: C.text }}>{fmtWindow(p!.target_time, tf?.minutes, tzMode)}</td>
                          <td className="px-4 py-2 text-right" style={{ ...MONO, color: C.green }}>{fmtPx(p!.predicted_high, digits)}</td>
                          <td className="px-4 py-2 text-right" style={{ ...MONO, color: C.red }}>{fmtPx(p!.predicted_low, digits)}</td>
                          <td className="px-4 py-2" style={{ color: trend.color }}>{trend.text}</td>
                          <td className="px-4 py-2" style={{ color: mom.color }}>{mom.text}</td>
                        </>
                      ) : (
                        <>
                          {[0, 1, 2, 3, 4].map(i => (
                            <td key={i} className={`px-4 py-2${i === 1 || i === 2 ? ' text-right' : ''}`} style={{ color: C.text }}>—</td>
                          ))}
                        </>
                      )}
                    </tr>
                  );
                })}
                {data.intraday.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-4 text-center" style={{ color: C.soft }}>No intraday predictions</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Daily price predictions */}
          {d && (
            <div className="rounded overflow-hidden" style={{ backgroundColor: C.panel, border: `1px solid ${C.border}` }}>
              <div className="px-4 py-2 text-sm font-semibold" style={{ color: C.text, backgroundColor: C.panel2, borderBottom: `1px solid ${C.border}` }}>
                Daily Price Predictions
              </div>
              <div className="grid gap-4 px-4 py-3" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                {([
                  ['Predicted High', d.predicted_high, C.green],
                  ['Predicted Low',  d.predicted_low,  C.red],
                  ['Predicted Range', d.predicted_range, C.text],
                  ['Midpoint',       d.predicted_midpoint, C.text],
                ] as const).map(([label, v, color]) => (
                  <div key={label}>
                    <div className="text-xs" style={{ color: C.soft }}>{label}</div>
                    <div className="text-base font-semibold" style={{ ...MONO, color }}>{fmtPx(v, digits)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trading levels */}
          {levels && (
            <div className="rounded overflow-hidden" style={{ backgroundColor: C.panel, border: `1px solid ${C.border}` }}>
              <div className="px-4 py-2 flex items-center gap-3" style={{ backgroundColor: C.panel2, borderBottom: `1px solid ${C.border}` }}>
                <span className="text-sm font-semibold" style={{ color: C.text }}>Trading Levels</span>
                <span className="px-2 py-0.5 rounded text-xs font-semibold"
                  style={{ border: `1px solid ${levels.side === 'SHORT' ? C.red : C.green}`, color: levels.side === 'SHORT' ? C.red : C.green }}>
                  {levels.side === 'SHORT' ? 'Short' : 'Long'}
                </span>
              </div>
              <div className="grid gap-3 p-3" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                {([
                  ['Take Profit', levels.tp,    C.green],
                  ['Entry',       levels.entry, C.teal],
                  ['Stop Loss',   levels.sl,    C.red],
                ] as const).map(([label, pair, color]) => (
                  <div key={label} className="rounded px-3 py-2" style={{ backgroundColor: C.panel2, border: `1px solid ${C.border}` }}>
                    <div className="text-xs font-semibold pb-1.5 mb-1.5" style={{ color: C.text, borderBottom: `1px solid ${C.border}` }}>{label}</div>
                    <div className="grid grid-cols-2 gap-2">
                      {(['Max', 'Min'] as const).map((k, i) => (
                        <div key={k}>
                          <div className="text-xs" style={{ color: C.soft }}>{k}</div>
                          <div className="text-base font-semibold" style={{ ...MONO, color }}>{pair[i]}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
